import { useEffect, useMemo, useRef, useState } from 'react';
import { LsCartDrawer } from '@/components/ls-cart-drawer/LsCartDrawer';
import { LsErrorBoundary } from '@/components/ls-error-boundary/LsErrorBoundary';
import { LsStepBar } from '@/components/ls-step-bar/LsStepBar';
import { DoneScreen } from '@/screens/done/DoneScreen';
import { ScreenRouter } from '@/app/routing/ScreenRouter';
import { useCartStore } from '@/features/cart/cart.store';
import { useCatalog } from '@/features/catalog/useCatalog';
import { useDesignStore } from '@/features/configurator/design.store';
import { useFlowStore } from '@/features/configurator/flow.store';
import { useSelectionStore } from '@/features/configurator/selection.store';
import { usePartner } from '@/features/partner/usePartner';
import { usePetStore } from '@/features/pet/pet.store';
import { brandFor } from '@/shared/content/brand';
import { icon } from '@/shared/content/icons';
import { COPY } from '@/shared/content/copy';
import { DEFAULT_COLOR } from '@/shared/content/garments';
import { PARTNER_SLUG } from '@/shared/lib/partner';
import type { Product } from '@/shared/api/schemas';
import { useIncomingDraft } from '@mini/features/draft/useIncomingDraft';
import { useBackButton } from '@mini/platform/useBackButton';
import { useTelegramShell } from '@mini/platform/useTelegramShell';
import { guardClosing } from '@mini/platform/telegram';
import { PayScreen } from '@mini/screens/pay/PayScreen';
import styles from './app.module.scss';

const BRAND = brandFor(PARTNER_SLUG);

/**
 * Мини-приложение телеграма.
 *
 * Оболочка своя, содержимое — общее с виджетом: те же экраны, те же
 * хранилища, тот же маршрутизатор. Разница ровно в трёх вещах: нет
 * обложки, кнопки «назад» и «дальше» принадлежат телеграму, а заказ
 * заканчивается оплатой, а не уходом на сторону.
 */
export function App() {
  const shell = useRef<HTMLDivElement>(null);
  const [root, setRoot] = useState<HTMLElement | null>(null);

  // Корень — это #ls-root: на нём живут меры заказчика, и масштаб
  // с размерами окна обязаны лежать там же, а не на нашей обёртке.
  useEffect(() => {
    setRoot(shell.current?.closest<HTMLElement>('#ls-root') ?? null);
  }, []);
  useTelegramShell(root);

  // Корзина, собранная на сайте, приезжает ключом в ссылке. Пока она
  // едет, показываем загрузку: иначе человек на долю секунды видит
  // пустую витрину и решает, что его заказ потерялся.
  const draft = useIncomingDraft();

  const { isLoading: isThemeLoading } = usePartner(PARTNER_SLUG);
  const { products: allProducts, prints, isLoading } = useCatalog(PARTNER_SLUG);

  const step = useFlowStore((s) => s.step);
  const route = useFlowStore((s) => s.route());
  const back = useFlowStore((s) => s.back);
  const jump = useFlowStore((s) => s.jump);
  const resetProgress = useFlowStore((s) => s.resetProgress);
  const isCartOpen = useFlowStore((s) => s.isCartOpen);
  const openCart = useFlowStore((s) => s.openCart);
  const closeCart = useFlowStore((s) => s.closeCart);

  const items = useCartStore((s) => s.items);

  // Считаем по самим позициям, а не вызовом count(): селектор по функции
  // стабилен, и счётчик в шапке не перерисовывался бы при добавлении.
  const count = items.reduce((sum, item) => sum + item.qty, 0);

  const startNew = useDesignStore((s) => s.startNew);
  const resetPet = usePetStore((s) => s.reset);

  const productId = useSelectionStore((s) => s.productId);
  const color = useSelectionStore((s) => s.color);
  const quantities = useSelectionStore((s) => s.quantities);
  const setProductId = useSelectionStore((s) => s.setProductId);
  const setColor = useSelectionStore((s) => s.setColor);
  const setQuantity = useSelectionStore((s) => s.setQuantity);
  const resetFor = useSelectionStore((s) => s.resetFor);
  const clearQuantities = useSelectionStore((s) => s.clearQuantities);

  const products = useMemo(
    () =>
      BRAND.garments
        ? allProducts.filter((item) => BRAND.garments?.includes(item.product_type))
        : allProducts,
    [allProducts],
  );

  const product = useMemo(
    () => products.find((item) => item.id === productId) ?? products[0] ?? null,
    [products, productId],
  );

  useEffect(() => {
    if (!productId && product) setProductId(product.id);
  }, [productId, product, setProductId]);

  useEffect(() => {
    if (!product || product.colors.length === 0) return;
    if (product.colors.includes(color)) return;

    setColor(product.colors[0] as string);
  }, [product, color, setColor]);

  // Корзина приехала с сайта целиком — человеку незачем идти по витрине
  // заново. Ведём его сразу к составу заказа, оттуда один шаг до формы.
  useEffect(() => {
    if (draft !== 'filled') return;
    if (!route.includes('done')) return;

    jump('done');
  }, [draft, route, jump]);

  // Свайп вниз закрывает мини-приложение мгновенно и без вопросов.
  // Пока в корзине пусто, терять нечего; как только появилась вещь —
  // телеграм спрашивает подтверждение.
  useEffect(() => guardClosing(items.length > 0), [items.length]);

  // «Назад» — телеграмная, своей стрелки в углу нет. На первом экране
  // её тоже нет: выход из приложения — это свайп вниз, и он уже есть.
  const isFirst = route.indexOf(step) <= 0;
  useBackButton(!isFirst, back);

  const backToCatalog = () => {
    closeCart();
    const shop = route[0];
    if (shop) jump(shop);

    // Пройденный путь обнуляется вместе с возвратом.
    //
    // Он хранится между сборками, и полоса шагов пускала человека
    // на «принт» или «размещение» прошлой вещи — туда, где теперь пусто.
    // Экран при этом открывался настоящий, но без содержимого, и выглядел
    // сломанным.
    resetProgress();
  };

  const beginCustom = () => {
    startNew();
    clearQuantities();
    resetPet();

    // Новая вещь — новый путь: шаги прошлой сборки пройденными не
    // считаются, и открывать их нечем.
    resetProgress();
  };

  const handlePickProduct = (value: Product) => {
    setProductId(value.id);
    resetFor(value.colors[0] ?? DEFAULT_COLOR);
  };

  return (
    <div className={styles['ls-mini']} ref={shell}>
      {/*
        Шапка — только знак заказчика. Счётчик «шаг N из M» здесь уже был
        и ушёл: шагов четыре, они и так нарисованы полосой внизу, а две
        записи об одном и том же человек читает как две разные.

        Знак один на всё приложение. Шапка шага рисует свой, и на экране
        оказывалось два одинаковых логотипа в двадцати точках друг
        от друга — см. LsStepHead, где он выключен для этой поверхности.
      */}
      <header className={styles['ls-mini__head']}>
        {BRAND.logoMiniUrl && (
          <img className={styles['ls-mini__logo']} src={BRAND.logoMiniUrl} alt="" />
        )}

        {/*
          Корзина в шапке — там, где её ищут. У магазина набор корзины
          идёт с витрины, и кнопка обязана быть на виду в тот момент,
          когда человек кладёт вещь: иначе он не видит, что вещь легла,
          и кладёт её второй раз.

          Счётчик показывается, только когда в корзине что-то есть:
          ноль на значке — это сообщение о пустоте, которое человек
          читает как ошибку.
        */}
        {BRAND.headerCart && (
          <button
            type="button"
            className={styles['ls-mini__cart']}
            aria-label={count > 0 ? `Корзина, ${count} шт.` : 'Корзина'}
            onClick={openCart}
          >
            <img className={styles['ls-mini__cart-icon']} src={icon('cart')} alt="" />
            {count > 0 && <span className={styles['ls-mini__cart-count']}>{count}</span>}
          </button>
        )}
      </header>

      <main className={styles['ls-mini__body']}>
        <LsErrorBoundary>
          {(isLoading || isThemeLoading || draft === 'loading') && (
            <p className={styles['ls-mini__state']}>{COPY.common.loading}</p>
          )}

          {!isLoading && !isThemeLoading && draft !== 'loading' && product && (
            <ScreenRouter
              partnerSlug={PARTNER_SLUG}
              products={products}
              prints={prints}
              allPrints={prints}
              product={product}
              artists={[]}
              color={color}
              quantities={quantities}
              brand={{
                ...BRAND,
                // Внутри телеграма уходить некуда: оформление здесь.
                miniappUrl: undefined,
              }}
              onPickArtist={() => undefined}
              onBuild={beginCustom}
              onPickProduct={handlePickProduct}
              onColorChange={setColor}
              onQuantityChange={setQuantity}
              onBackToCatalog={backToCatalog}
              screens={{
                // Состав заказа тот же, но ведёт он отсюда не наружу,
                // а на форму: оформление живёт здесь.
                done: (
                  <DoneScreen
                    logoUrl={BRAND.logoMiniUrl}
                    onBackToCatalog={backToCatalog}
                    actionLabel="Оформить"
                    lead="Заполни данные доставки и оплати заказ"
                    onAction={() => jump('order')}
                  />
                ),
                order: <PayScreen partnerSlug={PARTNER_SLUG} />,
              }}
            />
          )}
        </LsErrorBoundary>
      </main>

      <LsStepBar onCartClick={openCart} />

      <LsCartDrawer
        isOpen={isCartOpen}
        onClose={closeCart}
        onCheckout={() => {
          closeCart();
          jump('order');
        }}
        onBackToCatalog={backToCatalog}
      />
    </div>
  );
}
